<template>
  <div
    class="flex flex-col gap-1 rounded-l bg-error-container px-4 py-3.5"
    role="status"
  >
    <!--
      Red, and the only red in the app — see the note in pjazza.scss. It started
      as the green surface tint, but green means "ready" everywhere else in this
      system, so a green card reading "not taking orders" had the colour arguing
      with the words.

      This is the one container on the menu, which is the point: hairlines were
      too quiet for the only thing on the page a diner has to read before they
      start adding dishes nobody can make.

      Both lines are on-error-container rather than ink and muted. Muted
      (#6B7280) is under AA against any of these tints — fine on white, not
      here. Size and weight carry the hierarchy instead of a second colour.

      Comment kept inside the root: a leading one would make this component
      fragment-rooted and the caller's class would be silently dropped.
    -->
    <p class="text-title text-on-error-container">{{ copy.title }}</p>
    <p class="text-body text-on-error-container">{{ copy.body }}</p>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { OrderingMode } from '@/domains/pjazza/types/consts/OrderingMode.consts.ts'

import { ORDERING_MODE } from '@/domains/pjazza/types/consts/OrderingMode.consts.ts'
import { formatClockTime } from '@/domains/pjazza/utils/formatClockTime.ts'

/**
 * Why nothing can be ordered right now.
 *
 * Never an apology and never a dead end: the menu below stays fully browsable,
 * which is the point of saying this in a line of text rather than blocking the
 * screen.
 */
const props = defineProps<{
  variant: Extract<OrderingMode, typeof ORDERING_MODE.CLOSED | typeof ORDERING_MODE.MENU_ONLY>
  reopensAt?: string | null
}>()

const reopensLabel = computed(() => (props.reopensAt ? formatClockTime(props.reopensAt) : null))

const copy = computed(() => {
  if (props.variant === ORDERING_MODE.MENU_ONLY) {
    return {
      title: 'Browsing only for now',
      body: 'Have a look at the menu — ordering here is off at the moment.'
    }
  }

  return {
    title: 'Not taking orders right now',
    body: reopensLabel.value
      ? `Back at ${reopensLabel.value}. The menu is all here in the meantime.`
      : 'The menu is all here in the meantime.'
  }
})
</script>
